/**
 * Page sharing with organizations.
 *
 * Sharing is authoritative on the server (does not travel via PowerSync upload):
 * endpoints validate ownership and Postgres triggers mark
 * page.is_shared, which moves the page from the personal stream to the
 * organization stream for all members.
 */
import { createSignal } from "solid-js";
import { type components, generateId, queryKeys } from "@folio/shared";
import { api, createApiQuery, fetchApi } from "@/lib/api";

type OrganizationDTO = components["schemas"]["OrganizationDTO"];
type ErrorModel = components["schemas"]["ErrorModel"];

export function usePageSharing(pageId: () => string, options?: { enabled?: () => boolean }) {
  const [isToggling, setIsToggling] = createSignal<string | null>(null);

  const organizations = createApiQuery<OrganizationDTO[] | null, ErrorModel>(
    () => queryKeys.auth.organizations(),
    async () => api.GET("/api/org/listOrganizations", {}),
    () => ({ enabled: options?.enabled?.() ?? true }),
  );

  const sharedOrgIds = createApiQuery<string[] | null, ErrorModel>(
    () => queryKeys.pages.sharedOrgs(pageId()),
    async () =>
      api.GET("/api/sharing/getPageOrganizations", {
        params: { query: { pageId: pageId() } },
      }),
    () => ({ enabled: options?.enabled?.() ?? true }),
  );

  // P23 / CC-60 · I2: `?? []` turns "I don't know" into "not shared".
  // With `getPageOrganizations` down, the share dialog would claim the
  // document is NOT shared with any organization — and the user might
  // re-share it, or believe their draft is private when it is not.
  // `isSuccess` is the only condition under which this response is a TRUTH.
  const isShared = (organizationId: string) =>
    sharedOrgIds.isSuccess && sharedOrgIds.data.includes(organizationId);

  const toggleShare = async (organizationId: string) => {
    if (isToggling()) {
      return;
    }
    setIsToggling(organizationId);
    try {
      const request = isShared(organizationId)
        ? api.POST("/api/sharing/revokePageFromOrganization", {
            body: { pageId: pageId(), organizationId },
          })
        : api.POST("/api/sharing/sharePageWithOrganization", {
            body: { id: generateId(), pageId: pageId(), organizationId },
          });
      await fetchApi(request);
      await sharedOrgIds.refetch();
    } finally {
      setIsToggling(null);
    }
  };

  return { organizations, sharedOrgIds, isShared, toggleShare, isToggling };
}
